import { useEffect, useState } from 'react';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { apiClient } from '../../lib/api-client';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs';
import 'prismjs/components/prism-yaml';
import 'prismjs/themes/prism-tomorrow.css';
import yaml from 'js-yaml';
import { Skeleton } from '../ui/Skeleton';
import { GraphDomainsForm } from './graph-domains-form/GraphDomainsForm';
import {
  cloneDoc,
  graphDomainsDocToYaml,
  parseGraphDomainsYaml,
  type GraphDomainsDoc,
} from '../../lib/graph-domains-form-utils';

interface Props {
  open: boolean;
  onClose: () => void;
}

type EditorMode = 'form' | 'yaml';

export function MicrosoftGraphDomainsDialog({ open, onClose }: Props) {
  const [mode, setMode] = useState<EditorMode>('form');
  const [yamlContent, setYamlContent] = useState('');
  const [formDoc, setFormDoc] = useState<GraphDomainsDoc>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setMode('form');
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const load = async () => {
    setLoading(true);
    setError(null);
    setParseError(null);
    try {
      const res = await apiClient.getMicrosoftGraphDomainsRaw();
      const content = res?.yaml || '';
      setYamlContent(content);
      setFormDoc(parseGraphDomainsYaml(content || 'version: "1.0.0"\ndomains: {}'));
    } catch (e: any) {
      setError(e?.message || 'Failed to load domains YAML');
    } finally {
      setLoading(false);
    }
  };

  const validateYaml = (content: string): boolean => {
    try {
      yaml.load(content);
      setParseError(null);
      return true;
    } catch (err: any) {
      setParseError(`Invalid YAML: ${err.message}`);
      return false;
    }
  };

  const switchMode = (next: EditorMode) => {
    if (next === mode) return;

    if (next === 'yaml') {
      try {
        setYamlContent(graphDomainsDocToYaml(formDoc));
        setParseError(null);
      } catch (e: any) {
        setParseError(e.message);
        return;
      }
    }

    if (mode === 'yaml' && next === 'form') {
      try {
        setFormDoc(parseGraphDomainsYaml(yamlContent));
        setParseError(null);
      } catch (e: any) {
        setParseError(e.message);
        return;
      }
    }

    setMode(next);
  };

  const handleSave = async () => {
    let contentToSave = yamlContent;

    if (mode === 'form') {
      try {
        contentToSave = graphDomainsDocToYaml(formDoc);
        setParseError(null);
      } catch (e: any) {
        setParseError(e.message);
        return;
      }
    }

    if (!validateYaml(contentToSave)) return;

    setSaving(true);
    setError(null);
    try {
      await apiClient.saveMicrosoftGraphDomainsRaw(contentToSave);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to save domains YAML');
    } finally {
      setSaving(false);
    }
  };

  const handleYamlChange = (newValue: string) => {
    setYamlContent(newValue);
    try {
      yaml.load(newValue);
      setParseError(null);
    } catch (err: any) {
      setParseError(`Invalid YAML: ${err.message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent maxWidth="4xl" className="max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <DialogTitle>Microsoft Graph Domains Registry</DialogTitle>
              <DialogDescription>
                Domain-scoped Graph access for Notifications and Approvals (`config/microsoft-graph-domains.yaml`).
              </DialogDescription>
            </div>
            <div className="flex items-center border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shrink-0">
              <button
                type="button"
                onClick={() => switchMode('form')}
                className={`px-3 py-1.5 text-xs font-medium ${
                  mode === 'form'
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                Form
              </button>
              <button
                type="button"
                onClick={() => switchMode('yaml')}
                className={`px-3 py-1.5 text-xs font-medium ${
                  mode === 'yaml'
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                YAML
              </button>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3 min-h-0 overflow-y-auto">
          {loading && (
            <div className="space-y-2 py-4">
              <Skeleton className="h-64 w-full" />
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-200">
              {error}
            </div>
          )}

          {parseError && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-amber-800 dark:text-amber-200">
              {parseError}
            </div>
          )}

          {!loading && mode === 'form' && (
            <GraphDomainsForm doc={formDoc} onChange={(next) => setFormDoc(cloneDoc(next))} />
          )}

          {!loading && mode === 'yaml' && (
            <div className="relative">
              <div className="bg-gray-900 rounded-lg overflow-hidden border-2 border-gray-700 focus-within:border-blue-500">
                <div className="max-h-[min(560px,55vh)] overflow-auto custom-scrollbar">
                  <Editor
                    value={yamlContent}
                    onValueChange={handleYamlChange}
                    highlight={(code: string) => highlight(code, languages.yaml, 'yaml')}
                    padding={16}
                    style={{
                      fontFamily: '"Fira Code", "Fira Mono", monospace',
                      fontSize: 13,
                      backgroundColor: '#1e293b',
                      color: '#e2e8f0',
                      minHeight: 280,
                    }}
                    className="outline-none"
                  />
                </div>
              </div>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button onClick={onClose} disabled={saving} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading || !!parseError}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
